import { Injectable, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PerformanceRequestTrace } from '../../models/settings.model';

export interface AngularRouteTiming {
  path: string;
  title: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  dom_nodes?: number;
}

@Injectable({ providedIn: 'root' })
export class PerformanceDiagnosticsService {
  private readonly maxRequests = 80;
  private routeStartedAt = 0;

  readonly scopeId = signal(this.newScopeId());
  readonly clientRequests = signal<PerformanceRequestTrace[]>([]);
  readonly routeTiming = signal<AngularRouteTiming>({
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    title: typeof document !== 'undefined' ? document.title : 'Angular 4200',
    started_at: new Date().toISOString(),
  });

  constructor(router: Router) {
    this.routeStartedAt = this.now();
    router.events
      .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.scopeId.set(this.newScopeId());
          this.clientRequests.set([]);
          this.routeStartedAt = this.now();
          this.routeTiming.set({
            path: event.url,
            title: typeof document !== 'undefined' ? document.title : 'Angular 4200',
            started_at: new Date().toISOString(),
          });
          return;
        }
        const completedAt = this.now();
        this.routeTiming.set({
          path: event.urlAfterRedirects || event.url,
          title: typeof document !== 'undefined' ? document.title : 'Angular 4200',
          started_at: this.routeTiming().started_at,
          completed_at: new Date().toISOString(),
          duration_ms: Math.round(completedAt - this.routeStartedAt),
          dom_nodes: this.domNodeCount(),
        });
      });
  }

  beginRequest(method: string, url: string): { requestId: string; startedAt: number; startedIso: string; scopeId: string } {
    return {
      requestId: this.newRequestId(),
      startedAt: this.now(),
      startedIso: new Date().toISOString(),
      scopeId: this.scopeId(),
    };
  }

  recordRequest(trace: PerformanceRequestTrace): void {
    this.clientRequests.update(current => [trace, ...current].slice(0, this.maxRequests));
  }

  browserNavigationTiming(): Record<string, number | string> {
    if (typeof performance === 'undefined') return {};
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return {};
    return {
      navigation_to_load_ms: Math.round(nav.loadEventEnd || nav.duration || 0),
      redirect_ms: Math.round((nav.redirectEnd || 0) - (nav.redirectStart || 0)),
      request_to_first_byte_ms: Math.round(nav.responseStart - nav.requestStart),
      response_download_ms: Math.round(nav.responseEnd - nav.responseStart),
      dom_interactive_ms: Math.round(nav.domInteractive),
      dom_complete_ms: Math.round(nav.domComplete),
      resource_count: performance.getEntriesByType('resource').length,
      dom_nodes: this.domNodeCount(),
    };
  }

  environmentTiming(): Record<string, string | number> {
    const navAny = typeof navigator !== 'undefined' ? navigator as Navigator & {
      deviceMemory?: number;
      connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    } : null;
    const perfAny = typeof performance !== 'undefined' ? performance as Performance & {
      memory?: { usedJSHeapSize?: number };
    } : null;
    return {
      viewport: typeof window !== 'undefined' ? `${window.innerWidth} x ${window.innerHeight}` : 'n/a',
      cpu_cores: navAny?.hardwareConcurrency ?? 'n/a',
      device_memory_gb: navAny?.deviceMemory ?? 'n/a',
      network: navAny?.connection?.effectiveType ?? 'n/a',
      downlink_mbps: navAny?.connection?.downlink ?? 'n/a',
      rtt_ms: navAny?.connection?.rtt ?? 'n/a',
      js_heap_used: perfAny?.memory?.usedJSHeapSize ?? 'n/a',
    };
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private domNodeCount(): number {
    return typeof document !== 'undefined' ? document.getElementsByTagName('*').length : 0;
  }

  private newScopeId(): string {
    return 'angular-4200-' + Math.random().toString(16).slice(2) + '-' + Date.now().toString(16);
  }

  private newRequestId(): string {
    return 'ng-' + Math.random().toString(16).slice(2) + '-' + Date.now().toString(16);
  }
}
